import { RunElement } from '../../shared/run-element.js';
import { skinResolutionMixin } from '../../shared/skin-resolution-mixin.js';
import * as defaultSkin from './skins/checkbox.default.js';

export const CHECKBOX_SKINS = Object.freeze(['default', 'wireframe']);

class RunCheckbox extends skinResolutionMixin(RunElement) {
  #checked = false;
  #defaultChecked = false;
  #indeterminate = false;
  #disabled = false;
  #required = false;
  #name = '';
  #value = 'on';
  #skin = null;
  #label = null;
  #internals = null;

  static defaultSkin = defaultSkin;

  static skinLoaders = {
    wireframe: () => import('./skins/checkbox.wireframe.js'),
  };

  static formAssociated = true;

  static get observedAttributes() {
    return ['checked', 'disabled', 'required', 'name', 'value', 'indeterminate', 'skin', 'label'];
  }

  _onClickBound = (e) => this.#handleClick(e);
  _onKeydownBound = (e) => this.#handleKeydown(e);

  constructor() {
    super();
    this.#internals = this.attachInternals();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'checkbox');
    this.addEventListener('click', this._onClickBound);
    this.addEventListener('keydown', this._onKeydownBound);
    this.#syncFormValue();
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this._onClickBound);
    this.removeEventListener('keydown', this._onKeydownBound);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'checked':
        this.checked = newValue !== null;
        break;
      case 'disabled':
        this.disabled = newValue !== null;
        break;
      case 'required':
        this.required = newValue !== null;
        break;
      case 'name':
        this.name = newValue !== null ? newValue : '';
        break;
      case 'value':
        this.value = newValue !== null ? newValue : 'on';
        break;
      case 'indeterminate':
        this.indeterminate = newValue !== null;
        break;
      case 'skin':
        this.skin = newValue;
        break;
      case 'label':
        this.label = newValue;
        break;
    }
  }

  formResetCallback() {
    this.checked = this.#defaultChecked;
    this.indeterminate = false;
  }

  formDisabledCallback(disabled) {
    this.disabled = disabled;
  }

  get checked() {
    return this.#checked;
  }

  set checked(value) {
    const coerced = Boolean(value);
    if (this.#checked === coerced) return;
    this.#checked = coerced;
    if (coerced) {
      this.#indeterminate = false;
    }
    this.toggleAttribute('checked', coerced);
    this.#syncFormValue();
    this.#updateAria();
    this.requestUpdate();
  }

  get defaultChecked() {
    return this.#defaultChecked;
  }

  set defaultChecked(value) {
    const coerced = Boolean(value);
    if (this.#defaultChecked === coerced) return;
    this.#defaultChecked = coerced;
  }

  get indeterminate() {
    return this.#indeterminate;
  }

  set indeterminate(value) {
    const coerced = Boolean(value);
    if (this.#indeterminate === coerced) return;
    this.#indeterminate = coerced;
    this.toggleAttribute('indeterminate', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  get disabled() {
    return this.#disabled;
  }

  set disabled(value) {
    const coerced = Boolean(value);
    if (this.#disabled === coerced) return;
    this.#disabled = coerced;
    this.toggleAttribute('disabled', coerced);
    this.#syncFormValue();
    this.#updateAria();
    this.requestUpdate();
  }

  get required() {
    return this.#required;
  }

  set required(value) {
    const coerced = Boolean(value);
    if (this.#required === coerced) return;
    this.#required = coerced;
    this.toggleAttribute('required', coerced);
    this.#syncValidity();
    this.#updateAria();
    this.requestUpdate();
  }

  get name() {
    return this.#name;
  }

  set name(value) {
    const coerced = String(value ?? '').trim();
    if (this.#name === coerced) return;
    this.#name = coerced;
    if (coerced) {
      this.setAttribute('name', coerced);
    } else {
      this.removeAttribute('name');
    }
    this.requestUpdate();
  }

  get value() {
    return this.#value;
  }

  set value(value) {
    const coerced = String(value ?? 'on').trim() || 'on';
    if (this.#value === coerced) return;
    this.#value = coerced;
    this.setAttribute('value', coerced);
    this.#syncFormValue();
    this.requestUpdate();
  }

  get skin() { return this.#skin; }
  set skin(value) {
    const trimmed = value == null ? '' : String(value).trim();
    const coerced = trimmed || null;
    if (this.#skin === coerced) return;
    this.#skin = coerced;
    if (coerced) {
      this.setAttribute('skin', coerced);
    } else {
      this.removeAttribute('skin');
    }
    this.requestUpdate();
  }

  get label() { return this.#label; }
  set label(value) {
    const trimmed = value == null ? '' : String(value).trim();
    const coerced = trimmed || null;
    if (this.#label === coerced) return;
    this.#label = coerced;
    if (coerced) {
      this.setAttribute('label', coerced);
    } else {
      this.removeAttribute('label');
    }
    this.requestUpdate();
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    this.#updateAria();
  }

  #updateAria() {
    if (this.#indeterminate) {
      this.setAttribute('aria-checked', 'mixed');
    } else {
      this.setAttribute('aria-checked', String(this.#checked));
    }
    if (this.#disabled) {
      this.setAttribute('aria-disabled', 'true');
      this.setAttribute('tabindex', '-1');
    } else {
      this.removeAttribute('aria-disabled');
      this.setAttribute('tabindex', '0');
    }
    if (this.#required) {
      this.setAttribute('aria-required', 'true');
    } else {
      this.removeAttribute('aria-required');
    }
  }

  #syncFormValue() {
    if (this.#checked && !this.#disabled) {
      this.#internals.setFormValue(this.#value);
    } else {
      this.#internals.setFormValue(null);
    }
    this.#syncValidity();
  }

  #syncValidity() {
    if (this.#required && !this.#checked) {
      this.#internals.setValidity(
        { valueMissing: true },
        'Please check this box if you want to proceed.',
      );
    } else {
      this.#internals.setValidity({});
    }
  }

  #handleClick(event) {
    if (this.#disabled) {
      event.preventDefault();
      return;
    }
    this.#toggle();
  }

  #handleKeydown(event) {
    if (this.#disabled) return;
    if (event.key === ' ') {
      event.preventDefault();
      this.#toggle();
    }
  }

  #toggle() {
    if (this.#indeterminate) {
      this.indeterminate = false;
      this.checked = true;
    } else {
      this.checked = !this.#checked;
    }
    this.#emitChange();
  }

  #emitChange() {
    this.dispatchEvent(
      new CustomEvent('run-change', {
        bubbles: true,
        composed: true,
        detail: {
          checked: this.#checked,
          indeterminate: this.#indeterminate,
          value: this.#value,
        },
      }),
    );
  }
}

if (!customElements.get('run-checkbox')) {
  customElements.define('run-checkbox', RunCheckbox);
}

export { RunCheckbox };
export default RunCheckbox;